import { forwardRef, useId } from "react";
import { cn } from "@ds/lib/utils";

/**
 * Input — field teks/angka. State: default/focus/disabled/error.
 * Dukungan leftIcon (mis. search), label, dan pesan error/hint.
 * Untuk angka tabular gunakan prop `mono`.
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  mono?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, label, hint, error, leftIcon, rightSlot, mono, id, ...props },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const describedBy = error
      ? `${inputId}-error`
      : hint
        ? `${inputId}-hint`
        : undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-[0.8125rem] font-medium text-ink"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <span
              className="pointer-events-none absolute left-3 flex text-muted [&>svg]:size-4"
              aria-hidden
            >
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              "h-10 w-full rounded-md border bg-bg px-3 text-sm text-ink",
              "placeholder:text-muted transition-colors duration-150",
              "focus-visible:border-primary focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
              leftIcon && "pl-9",
              rightSlot && "pr-10",
              mono && "font-mono tabular-nums",
              error && "border-danger focus-visible:border-danger",
              className,
            )}
            {...props}
          />
          {rightSlot && (
            <span className="absolute right-2 flex items-center">{rightSlot}</span>
          )}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="text-[0.8125rem] text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-[0.8125rem] text-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
